import { useState } from 'react';
import { Formik } from 'formik';
import { useRouter } from '@uirouter/react';

import { notifyError, notifySuccess } from '@/portainer/services/notifications';

import { validateForm } from '@@/form-components/validate-form';
import { confirm } from '@@/modals/confirm';
import { Modal, ModalType } from '@@/modals';
import { ModalBody } from '@@/modals/Modal/ModalBody';
import { ModalHeader } from '@@/modals/Modal/ModalHeader';
import { buildConfirmButton } from '@@/modals/utils';

import { Stack } from '../../../../../common/stacks/types';
import { useStackConversion } from '../../../../../common/stacks/queries/useStackConversion';

import { FormSubmitValues } from './StackDuplicationForm.types';
import { StackConversionPreviewModal } from './StackConversionPreviewModal';
import { StackDuplicationFormInner } from './StackDuplicationFormInner';
import {
  getBaseValidationSchema,
  getDuplicateValidationSchema,
  getMigrateValidationSchema,
} from './StackDuplicationForm.validation';
import { useDuplicateStackMutation } from './useDuplicateStackMutation';
import { useMigrateStackMutation } from './useMigrateStackMutation';

interface StackDuplicationFormProps {
  currentEnvironmentId: number;

  yamlError?: string;

  originalFileContent: string;
  stack: Stack;
  isOpen: boolean;
  onClose: () => void;
}

export function StackDuplicationForm({
  yamlError,
  originalFileContent,
  currentEnvironmentId,
  stack,
  isOpen,
  onClose,
}: StackDuplicationFormProps) {
  const router = useRouter();
  const duplicateMutation = useDuplicateStackMutation();
  const migrateMutation = useMigrateStackMutation();
  const convertMutation = useStackConversion();
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const initialValues: FormSubmitValues = {
    environmentId: undefined,
    newName: '',
    isHelm: false,
    namespace: 'default',
    actionType: 'migrate', // Default value, will be set by button clicks
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      onDismiss={onClose}
      size="lg"
      aria-label="Stack duplication / migration"
      className="!overflow-visible"
    >
      <ModalHeader title="Stack duplication / migration" />
      <ModalBody>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validateOnMount
          validationSchema={getBaseValidationSchema()}
        >
          <StackDuplicationFormInner
            yamlError={yamlError}
            currentEnvironmentId={currentEnvironmentId}
            currentStackName={stack.Name}
            stackType={stack.Type}
          />
        </Formik>
      </ModalBody>
      <StackConversionPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        conversionData={convertMutation.data}
        isLoading={convertMutation.isLoading}
        error={convertMutation.error as Error | null}
      />
    </Modal>
  );

  async function handleSubmit(values: FormSubmitValues) {
    const { actionType, environmentId, newName, isHelm } = values;

    switch (actionType) {
      case 'duplicate':
        await handleDuplicate(environmentId!, newName);
        break;
      case 'migrate':
        await handleMigrate(
          environmentId!,
          newName,
          values.namespace,
          isHelm,
          values.isKubernetesTarget
        );
        break;
      case 'preview':
        await handlePreview(isHelm, values.namespace);
        break;
      default:
        break;
    }
  }

  async function handleDuplicate(environmentId: number, name: string) {
    const schema = getDuplicateValidationSchema();
    const errors = await validateForm(() => schema, { environmentId, name });
    if (errors) {
      notifyError(
        'Validation Error',
        undefined,
        'Please fix the errors and try again.'
      );
      return;
    }

    duplicateMutation.mutate(
      {
        fileContent: originalFileContent,
        name,
        type: stack.Type,
        env: stack.Env,
        targetEnvironmentId: environmentId,
      },
      {
        onSuccess() {
          notifySuccess('Success', 'Stack successfully duplicated');
          router.stateService.go('docker.stacks', {}, { reload: true });
        },
        onError(error) {
          notifyError('Failure', error as Error, 'Unable to duplicate stack');
        },
      }
    );
  }

  async function handleMigrate(
    environmentId: number,
    name: string | undefined,
    namespace?: string,
    isHelm?: boolean,
    isKubernetesTarget?: boolean
  ) {
    const isRename = environmentId === currentEnvironmentId;

    const message = getMigrateConfirmMessage(isRename, isKubernetesTarget);

    const confirmed = await confirm({
      title: 'Are you sure?',
      modalType: ModalType.Warn,
      message,
      confirmButton: buildConfirmButton(
        isRename ? 'Rename' : 'Migrate',
        'danger'
      ),
    });

    if (!confirmed) {
      return;
    }

    const schema = getMigrateValidationSchema(stack.Name, currentEnvironmentId);
    const errors = await validateForm(() => schema, {
      environmentId,
      name,
    });

    if (errors) {
      notifyError(
        'Validation Error',
        undefined,
        'Please fix the errors and try again.'
      );
      return;
    }

    migrateMutation.mutate(
      {
        name,
        stackType: stack.Type,
        fromEnvId: currentEnvironmentId,
        id: stack.Id,
        targetEnvId: environmentId,
        fromSwarmId: stack.SwarmId,
        namespace,
        isHelm,
        isKubernetesTarget,
      },
      {
        onSuccess() {
          notifySuccess('Stack successfully migrated', name || stack.Name);
          router.stateService.go('docker.stacks', {}, { reload: true });
        },
        onError(error) {
          notifyError('Failure', error as Error, 'Unable to migrate stack');
        },
      }
    );
  }

  async function handlePreview(isHelm: boolean, namespace?: string) {
    convertMutation.mutate(
      {
        stackId: stack.Id,
        payload: {
          targetFormat: isHelm ? 'helm' : 'kubernetes',
          namespace: namespace || 'default',
        },
      },
      {
        onSuccess() {
          setIsPreviewModalOpen(true);
        },
        onError(error) {
          notifyError('Failure', error as Error, 'Unable to convert stack');
        },
      }
    );
  }
}

function getMigrateConfirmMessage(
  isRename: boolean,
  isKubernetesTarget?: boolean
) {
  if (isRename) {
    return 'This action will deploy a new instance of this stack with the new name that will replace the current stack. Please note that this does NOT migrate the content of any persistent volumes that may be attached to this stack.';
  }

  if (isKubernetesTarget) {
    return (
      <>
        <p>
          This action will deploy a new instance of this stack to the target
          Kubernetes environment. Docker volumes will be converted to
          PersistentVolumeClaims, but their data will <strong>NOT</strong> be
          migrated.
        </p>
        <p className="text-warning mt-2 font-bold">
          Ensure all Docker volume data is backed up before proceeding.
        </p>
      </>
    );
  }

  return 'This action will deploy a new instance of this stack on the target environment, please note that this does NOT relocate the content of any persistent volumes that may be attached to this stack.';
}
